import { ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  CameraIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import React, { ReactNode, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { ChatBubbleMultipleIcon } from '@/components/icons/ChatBubbleMultiple.icon';
import { KeyIcon } from '@/components/icons/Key.icon';
import { ImageModal } from '@/components/markdown/ImageModal';
import { Table } from '@/components/table/Table';
import { Column } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { SearchBar } from '@/components/ui/SearchBar';
import { useDownloadFile, useMy } from '@/hooks';
import { useGlobalState } from '@/state/global.state';
import { Operator, Risk, Webpage } from '@/types';

interface WebApplicationSitemapProps {
  asset: { key: string; identifier: string };
}

const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return 'Unknown';
  }
};

const WebApplicationSitemap: React.FC<WebApplicationSitemapProps> = ({
  asset,
}) => {
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState<keyof WebpageWithRisk>('url');
  const [descending, setDescending] = useState(false);

  const [debouncedSearch] = useDebounce(search, 500);

  const {
    data: webpageTree,
    status: webpagesStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMy(
    {
      resource: 'webpage',
      tree: true,
      query: {
        limit: 100,
        orderBy: orderBy,
        descending: descending,
        node: {
          labels: ['Webpage'],
          filters: [
            ...(debouncedSearch
              ? [
                  {
                    field: 'url',
                    operator: 'CONTAINS' as Operator,
                    value: debouncedSearch,
                  },
                ]
              : []),
          ],
          relationships: [
            {
              label: 'HAS_WEBPAGE',
              source: {
                labels: ['Asset'],
                filters: [
                  {
                    field: 'key',
                    operator: '=',
                    value: asset.key,
                  },
                ],
              },
            },
            {
              optional: true,
              label: 'HAS_VULNERABILITY',
              select_one: true,
              target: {
                labels: ['Risk'],
              },
            },
          ],
        },
      },
    },
    { enabled: !!asset?.key }
  );

  const webpages = useMemo(() => {
    return (webpageTree ?? [])
      .map(node => {
        const webpage = node?.node?.result?.webpages?.[0];

        if (!webpage) return undefined;

        return {
          ...webpage,
          risk: node?.node?.relationships?.[1]?.target?.result?.risks?.[0],
        };
      })
      .filter(Boolean) as WebpageWithRisk[];
  }, [webpageTree]);

  const { mutateAsync: downloadFile } = useDownloadFile();
  const { drawerState } = useGlobalState();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const imageSrcs = useMemo(() => {
    return (webpages || [])
      .map(wp => wp.screenshot)
      .filter(Boolean) as string[];
  }, [webpages]);

  const primaryHostname = useMemo(() => {
    return getHostname(asset.identifier);
  }, [asset.identifier]);

  const { internalWebpages, externalWebpages } = useMemo(() => {
    const internal: WebpageWithRisk[] = [];
    const external: WebpageWithRisk[] = [];

    for (const wp of webpages) {
      const hostname = getHostname(wp.url);
      if (hostname === primaryHostname) {
        internal.push(wp);
      } else {
        external.push(wp);
      }
    }

    return { internalWebpages: internal, externalWebpages: external };
  }, [webpages, primaryHostname]);

  /**
   * Shared columns for both tables (everything except URL column)
   */
  const sharedColumns: Column<WebpageWithRisk>[] = useMemo(
    () => [
      {
        id: '',
        label: 'Secrets',
        sorting: false,
        fixedWidth: 75,
        align: 'center',
        cell: wp =>
          wp.risk?.name?.startsWith('web-secret-') ? (
            <Tooltip title="Secrets identified - click to view vulnerabilities">
              <div
                className="cursor-pointer"
                onClick={() => {
                  if (wp.risk?.key) {
                    drawerState.handleOpenVulnerabilityDrawer(wp.risk);
                  }
                }}
                role="button"
              >
                <KeyIcon className="text-chariot-brand-primary size-5" />
              </div>
            </Tooltip>
          ) : null,
      },
      {
        id: '',
        label: 'Login',
        fixedWidth: 60,
        align: 'center',
        sorting: false,
        cell: wp =>
          wp.type === 'authentication' ? (
            <Tooltip title="Login form identified - click to view vulnerabilities">
              <div
                className="cursor-pointer"
                onClick={() => {
                  if (wp.risk?.key) {
                    drawerState.handleOpenVulnerabilityDrawer(wp.risk);
                  }
                }}
                role="button"
              >
                <CheckCircleIcon className="text-chariot-brand-primary size-5" />
              </div>
            </Tooltip>
          ) : null,
      },
      {
        id: '',
        label: 'SSO',
        fixedWidth: 50,
        align: 'center',
        sorting: false,
        cell: wp => {
          const hasSSO = Object.keys(wp.sso_identified || {}).length > 0;
          const loginIdentified = wp.type === 'authentication';

          if (hasSSO) {
            return (
              <Tooltip title="SSO protection identified">
                <CheckCircleIcon className="text-chariot-brand-primary size-5" />
              </Tooltip>
            );
          }

          if (loginIdentified) {
            return (
              <Tooltip title="No SSO protection identified">
                <XMarkIcon className="text-chariot-text-disabled size-5 stroke-[3px]" />
              </Tooltip>
            );
          }

          return null;
        },
      },
      {
        id: '',
        label: 'Screenshots',
        fixedWidth: 100,
        align: 'center',
        sorting: false,
        cell: wp => {
          return wp.screenshot ? (
            <Tooltip title="Screenshot available - click to view">
              <CameraIcon
                className="text-chariot-brand-primary size-5 cursor-pointer stroke-[3px]"
                onClick={() => {
                  const index = (webpages || []).findIndex(
                    w => w.key === wp.key
                  );
                  if (index === undefined) return;
                  setImageModalOpen(true);
                  setImageIndex(index);
                }}
              />
            </Tooltip>
          ) : null;
        },
      },
      {
        id: '',
        label: 'Response',
        fixedWidth: 90,
        sorting: false,
        cell: wp => {
          if (wp.details_filepath) {
            return (
              <div
                className="border-chariot-brand-primary bg-chariot-background-secondary text-chariot-brand-tertiary flex w-fit cursor-pointer items-center gap-2 rounded-full border py-1 pr-2 pl-3"
                onClick={event => {
                  event.stopPropagation();
                  downloadFile({
                    name: wp.details_filepath,
                    downloadName: `${asset.identifier}-webpage-${wp.key.split('#').pop()}.html`,
                  });
                }}
                role="button"
              >
                <ChatBubbleMultipleIcon className="size-4" />
                <ChevronRightIcon className="size-4 stroke-[3px]" />
              </div>
            );
          }
          return null;
        },
      },
    ],
    [webpages, asset.identifier, downloadFile, drawerState]
  );

  /**
   * Internal sitemap columns - shows path only
   */
  const internalColumns: Column<WebpageWithRisk>[] = useMemo(
    () => [
      {
        id: 'url',
        label: 'Path',
        sorting: true,
        isExpandable: { minWidth: 200 },
        onClick: wp => {
          window.open(wp.url, '_blank', 'noopener,noreferrer');
        },
        cell: (wp): ReactNode => {
          try {
            const urlObj = new URL(wp.url);
            return urlObj.pathname + urlObj.search + urlObj.hash;
          } catch {
            return wp.url;
          }
        },
      },
      ...sharedColumns,
    ],
    [sharedColumns]
  );

  /**
   * External URLs columns - shows full URL
   */
  const externalColumns: Column<WebpageWithRisk>[] = useMemo(
    () => [
      {
        id: 'url',
        label: 'URL',
        sorting: true,
        isExpandable: { minWidth: 200 },
        onClick: wp => {
          window.open(wp.url, '_blank', 'noopener,noreferrer');
        },
        cell: (wp): ReactNode => wp.url,
      },
      ...sharedColumns,
    ],
    [sharedColumns]
  );

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto">
      <SearchBar
        size="sm"
        className="h-fit w-64 shrink-0 p-1 pb-0"
        value={search}
        onChange={setSearch}
        placeholder="Search"
        disabled={webpagesStatus === 'pending'}
      />

      {/* Internal Sitemap Table */}
      <div className="flex flex-col gap-2">
        <h3 className="text-chariot-text-secondary text-sm font-semibold">
          Sitemap ({internalWebpages.length})
        </h3>
        <Table<WebpageWithRisk>
          name="web-application-sitemap-internal"
          columns={internalColumns}
          data={internalWebpages}
          selection={{ hide: true }}
          status={webpagesStatus}
          sort={{
            orderBy,
            descending,
            onSort: (sort: {
              orderBy: keyof WebpageWithRisk;
              descending: boolean;
            }) => {
              setOrderBy(sort.orderBy);
              setDescending(sort.descending);
            },
          }}
          noData={{
            title: debouncedSearch
              ? 'No pages match your search.'
              : 'No Pages Found',
          }}
          loadingRowCount={5}
          error={null}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>

      {/* External URLs Table - only show if there are external URLs */}
      {externalWebpages.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-chariot-text-secondary text-sm font-semibold">
            External URLs ({externalWebpages.length})
          </h3>
          <Table<WebpageWithRisk>
            name="web-application-sitemap-external"
            columns={externalColumns}
            data={externalWebpages}
            selection={{ hide: true }}
            status={webpagesStatus}
            sort={{
              orderBy,
              descending,
              onSort: (sort: {
                orderBy: keyof WebpageWithRisk;
                descending: boolean;
              }) => {
                setOrderBy(sort.orderBy);
                setDescending(sort.descending);
              },
            }}
            noData={{
              title: 'No External URLs Found',
            }}
            loadingRowCount={3}
            error={null}
          />
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        open={imageModalOpen}
        setOpen={setImageModalOpen}
        imageSrcs={imageSrcs}
        imageIndex={imageIndex}
        setImageIndex={setImageIndex}
      />
    </div>
  );
};

export default WebApplicationSitemap;

interface WebpageWithRisk extends Webpage {
  risk?: Risk;
}
